import * as React from 'react'
import { Outlet, useParams } from 'react-router-dom'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { GetFeatureAvailabilitiesDocument } from 'generated/resources/graphql'
import { isEmpty } from 'lodash'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'

import CollectionsBootstrap from 'modules/performance_process/lib/CollectionsBootstrap'

export interface NewPerformanceFeatureAvailability {
  canSeeGoalsV2: boolean
  canSeeReviewProcess: boolean
}

export interface ContextProps {
  newPerformanceFeatureAvailability: NewPerformanceFeatureAvailability
  hasCustomScoreConfigurationEnabled: boolean
}

const NewPerformanceModuleContext = React.createContext<ContextProps | null>(null)

export const useNewPerformanceModuleContext = () => {
  const context = React.useContext(NewPerformanceModuleContext)
  if (!context) {
    throw new Error('NewPerformanceModuleContext must be within its provider')
  }
  return context
}

const ReviewsRootPage = () => {
  const { company: currentCompany } = useCurrentSession()
  const params = useParams()

  const { loading: loadingFeatures, data: featuresData } = useQuery(
    GetFeatureAvailabilitiesDocument,
    {
      fetchPolicy: 'cache-first',
      variables: {
        modules: [AvailableModules.review_process, AvailableModules.goals],
        companyId: currentCompany.id,
      },
    }
  )

  useEffect(() => {
    // Ensure we only track the first time the user visits the reviews module
    if (isEmpty(params)) track(trackingEvents.performanceProcess.goToPerformanceReviews)
  }, [])

  if (loadingFeatures)
    return (
      <Box width="full" alignItems="center" height="full" justifyContent="center">
        <Spinner />
      </Box>
    )

  /**
   * Review Process Feature with paywall
   */
  const featureAvailabilities = featuresData?.companies.featureAvailabilities ?? []
  const hasReviewProcess = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.review_process_core
  )

  /**
   * Goals V2 Feature with paywall
   */
  const hasGoalsV2 = isFeatureAvailabilityEnabled(featureAvailabilities, Features.goals_v2_core)
  const hasCustomScoreConfigurationEnabled = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.review_process_custom_score_configuration
  )

  const newPerformanceFeatureAvailability: NewPerformanceFeatureAvailability = {
    canSeeGoalsV2: hasGoalsV2,
    canSeeReviewProcess: hasReviewProcess,
  }

  return (
    <CollectionsBootstrap>
      <AuthorizeUser authorizeFn={() => hasReviewProcess || hasGoalsV2}>
        <NewPerformanceModuleContext.Provider
          value={{
            newPerformanceFeatureAvailability: newPerformanceFeatureAvailability,
            hasCustomScoreConfigurationEnabled: hasCustomScoreConfigurationEnabled,
          }}
        >
          <Outlet />
        </NewPerformanceModuleContext.Provider>
      </AuthorizeUser>
    </CollectionsBootstrap>
  )
}

export default ReviewsRootPage
